export const ORGANIZATION_SIZES = [
  '1 to 4',
  '5 to 9',
  '10 to 49',
  '50 to 249',
  '250 to 499',
  '500 to 999',
  '1000+'
] as const
export type OrganizationSize = (typeof ORGANIZATION_SIZES)[number]

export const SP_RATINGS = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-'] as const
export type SpRating = (typeof SP_RATINGS)[number]

export const FITCH_RATINGS = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-'] as const
export type FitchRating = (typeof FITCH_RATINGS)[number]

export const JCR_RATINGS = ['AAA', 'AA+', 'AA', 'AA-', 'A+', 'A', 'A-', 'BBB+', 'BBB', 'BBB-'] as const
export type JcrRating = (typeof JCR_RATINGS)[number]

export const MOODYS_RATINGS = ['Aaa', 'Aa1', 'Aa2', 'Aa3', 'A1', 'A2', 'A3', 'Baa1', 'Baa2', 'Baa3'] as const
export type MoodysRating = (typeof MOODYS_RATINGS)[number]

export const ALLIANZ_RATINGS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'] as const
export type AllianzRating = (typeof ALLIANZ_RATINGS)[number]

export const ALLIANZ_RATINGS_ELIGIBLE = ['1', '2', '3', '4', '5', '6', '7'] as const
export type AllianzRatingEligible = (typeof ALLIANZ_RATINGS_ELIGIBLE)[number]

export const CREDIT_CLASSIFICATION_IG = 'IG' as const
export const CREDIT_CLASSIFICATION_INSURED = 'Insured' as const
export const CREDIT_CLASSIFICATIONS = [CREDIT_CLASSIFICATION_IG, CREDIT_CLASSIFICATION_INSURED] as const
export type CreditClassification = (typeof CREDIT_CLASSIFICATIONS)[number]

export const RATING_SCALE_IG = 'IG' as const
export const RATING_SCALE_ALLIANZ = 'Allianz' as const
export const RATING_SCALES = [RATING_SCALE_IG, RATING_SCALE_ALLIANZ] as const
export type RatingScale = (typeof RATING_SCALES)[number]

export const KLEAR_ORGANIZATION_ID = 1 as const
export const OFF_PLATFORM_ACCOUNT_ID = -1 as const

export const ORDER_ID_AUTO_INCREMENT = 5823
export const INVOICE_ID_AUTO_INCREMENT = 12433

export const DEFAULT_FINANCIAL_HISTORY_WINDOW_RANGE = 90 as const

export const US_BANK_ACCOUNT_TYPE = 'us_bank_account' as const

export const AUTH_COOKIE_NAME = 'klear-session'

export const E_SIGNATURE_CONSENT_TEXT = 'I consent to use electronic signatures for this agreement'

export const ESIGN_CONSENT_EVENT_CHECKED = 'esign_consent_checked'
export const ESIGN_CONSENT_EVENT_UNCHECKED = 'esign_consent_unchecked'

export const CHECKBOX_CONSENT_PREFIX = 'I have read and agree to the updated '

export const ROLE_KLEAR_SUPER_ADMIN = 'klear_super_admin'
export const ROLE_KLEAR_CSM = 'klear_csm'
export const ROLE_KLEAR_READONLY = 'klear_readonly'
export const ROLE_KLEAR_FUNDER = 'klear_funder'
export const ROLE_TRADER_ADMIN = 'trader_admin'
export const ROLE_TRADER_FINANCIAL_ADMIN = 'trader_admin_financial'
export const ROLE_TRADER_ORGANIZATION_OWNER = 'trader_organization_owner'
export const ROLE_TRADER_USER = 'trader_user'
export const ROLE_TRADER_READONLY = 'trader_readonly'
export const ROLE_TRADER_ACCOUNTS_PAYABLE = 'trader_accounts_payable'
export type TraderRole =
  | typeof ROLE_TRADER_ADMIN
  | typeof ROLE_TRADER_FINANCIAL_ADMIN
  | typeof ROLE_TRADER_ORGANIZATION_OWNER
  | typeof ROLE_TRADER_USER
  | typeof ROLE_TRADER_READONLY
  | typeof ROLE_TRADER_ACCOUNTS_PAYABLE

export const TRADER_ROLES_THAT_CAN_MANAGE_ORGAN_USERS: readonly string[] = [ROLE_TRADER_ORGANIZATION_OWNER]

export const KLEAR_ROLES = [ROLE_KLEAR_SUPER_ADMIN, ROLE_KLEAR_CSM, ROLE_KLEAR_READONLY, ROLE_KLEAR_FUNDER] as const

export type KlearRole = (typeof KLEAR_ROLES)[number]

export function isKlearRole(role: string | undefined | null): role is KlearRole {
  return role != null && (KLEAR_ROLES as readonly string[]).includes(role)
}

export const ROLE_LABELS = {
  [ROLE_KLEAR_SUPER_ADMIN]: 'Super Admin',
  [ROLE_KLEAR_CSM]: 'CSM',
  [ROLE_KLEAR_READONLY]: 'Manager',
  [ROLE_KLEAR_FUNDER]: 'Funder',
  [ROLE_TRADER_ADMIN]: 'Admin',
  [ROLE_TRADER_FINANCIAL_ADMIN]: 'Financial Admin',
  [ROLE_TRADER_ORGANIZATION_OWNER]: 'Owner',
  [ROLE_TRADER_USER]: 'User',
  [ROLE_TRADER_READONLY]: 'Read Only',
  [ROLE_TRADER_ACCOUNTS_PAYABLE]: 'Accounts Payable'
}

export const TRADER_ROLE_ACCOUNT_LIMITS: Readonly<Partial<Record<TraderRole, number>>> = {
  [ROLE_TRADER_ORGANIZATION_OWNER]: 1,
  [ROLE_TRADER_FINANCIAL_ADMIN]: 3
}
export const PERMISSION_ACTION_ADD = 'add'
export const PERMISSION_ACTION_VIEW = 'view'
export const PERMISSION_ACTION_EDIT = 'edit'
export const PERMISSION_ACTION_DELETE = 'delete'
export const PERMISSION_ACTION_RESET_PASSWORD = 'reset_password'
export const PERMISSION_ACTION_WELCOME_EMAIL = 'welcome_email'
export const PERMISSION_ACTION_PUBLISH = 'publish'
export const PERMISSION_ACTION_VOID = 'void'
export const PERMISSION_ACTION_ACCEPT = 'accept'
export const PERMISSION_ACTION_REJECT = 'reject'
export const PERMISSION_ACTION_APPROVE = 'approve'
export const PERMISSION_ACTION_DENY = 'deny'
export const PERMISSION_ACTION_FUND = 'fund'
export const PERMISSION_ACTION_COMPLETE = 'complete'
export const PERMISSION_ACTION_EXPIRE = 'expire'
export const PERMISSION_ACTION_RESET = 'reset'

export const ORDER_EVENT_ACTIONS = [
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ACTION_VOID,
  PERMISSION_ACTION_ACCEPT,
  PERMISSION_ACTION_REJECT,
  PERMISSION_ACTION_APPROVE,
  PERMISSION_ACTION_DENY,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_EXPIRE,
  PERMISSION_ACTION_RESET
] as const

export type PermissionAction =
  | typeof PERMISSION_ACTION_ADD
  | typeof PERMISSION_ACTION_VIEW
  | typeof PERMISSION_ACTION_EDIT
  | typeof PERMISSION_ACTION_DELETE
  | typeof PERMISSION_ACTION_PUBLISH
  | typeof PERMISSION_ACTION_VOID
  | typeof PERMISSION_ACTION_ACCEPT
  | typeof PERMISSION_ACTION_REJECT
  | typeof PERMISSION_ACTION_EXPIRE
  | typeof PERMISSION_ACTION_RESET
  | typeof PERMISSION_ACTION_APPROVE
  | typeof PERMISSION_ACTION_DENY
  | typeof PERMISSION_ACTION_FUND
  | typeof PERMISSION_ACTION_COMPLETE
  | typeof PERMISSION_ACTION_RESET_PASSWORD
  | typeof PERMISSION_ACTION_WELCOME_EMAIL
export const PERMISSION_ENTITY_USER = 'user'
export const PERMISSION_ENTITY_ROLE = 'role'
export const PERMISSION_ENTITY_ORGANIZATION = 'organization'
export const PERMISSION_ENTITY_TREASURY = 'treasury'
export const PERMISSION_ENTITY_SETTINGS = 'settings'

export const PERMISSION_ENTITY_CUSTOMER = 'customer'
export const PERMISSION_ENTITY_CREDIT = 'credit'
export const PERMISSION_ENTITY_TRADER = 'trader'
export const PERMISSION_ENTITY_SUPPLIER = 'supplier'
export const PERMISSION_ENTITY_FUNDER = 'funder'

export const PERMISSION_ENTITY_ORDER = 'order'

export const PERMISSION_ENTITY_PURCHASE_ORDER = 'purchase_order'

export const PERMISSION_ENTITY_OPERATIONAL = 'operational'

export const PERMISSION_ENTITY_INVOICE = 'invoice'
export const PERMISSION_ENTITY_INVOICE_OFFER = 'invoice_offer'
export const PERMISSION_ENTITY_INVOICE_TRANSACTION = 'invoice_transaction'
export const PERMISSION_ENTITY_PAAS_OFFER = 'paas_offer'
export const PERMISSION_ENTITY_PAYMENT = 'payment'
export const PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE = 'proof_of_acceptance'
export const PERMISSION_ENTITY_SOURCE_DOCUMENT = 'source_document'
export const PERMISSION_ENTITY_DOCUMENTS = 'documents'
export const PERMISSION_ENTITY_REPORTING = 'reporting'
export const PERMISSION_ENTITY_PLATFORM_FEE = 'platform_fee'
export const PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE = 'external_buffer_fee'
export const PERMISSION_ENTITY_LATE_FEE = 'late_fee'
export const FEE_ENTITY_TYPES = [
  PERMISSION_ENTITY_PLATFORM_FEE,
  PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE,
  PERMISSION_ENTITY_LATE_FEE
] as const
export type FeeEntityType = (typeof FEE_ENTITY_TYPES)[number]

export const FEE_ENTITY_TYPE_LABEL: Record<FeeEntityType, string> = {
  [PERMISSION_ENTITY_PLATFORM_FEE]: 'Platform fee',
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: 'External buffer fee',
  [PERMISSION_ENTITY_LATE_FEE]: 'Late fee'
}

export const PERMISSION_ENTITY_SPLIT_ADJUSTMENT = 'split_adjustment'

export const PERMISSION_ENTITY_LEGAL_AGREEMENT_ACCEPTANCE = 'legal_agreement_acceptance'

export const ORDER_EVENT_ENTITIES = [
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_PAYMENT,
  PERMISSION_ENTITY_PLATFORM_FEE,
  PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE,
  PERMISSION_ENTITY_LATE_FEE,
  PERMISSION_ENTITY_SPLIT_ADJUSTMENT
] as const

export const PERMISSION_ENTITY_CHANGE_LOG = 'change_log'

export type PermissionEntity =
  | typeof PERMISSION_ENTITY_USER
  | typeof PERMISSION_ENTITY_ROLE
  | typeof PERMISSION_ENTITY_ORGANIZATION
  | typeof PERMISSION_ENTITY_TREASURY
  | typeof PERMISSION_ENTITY_TRADER
  | typeof PERMISSION_ENTITY_SETTINGS
  | typeof PERMISSION_ENTITY_CUSTOMER
  | typeof PERMISSION_ENTITY_CREDIT
  | typeof PERMISSION_ENTITY_SUPPLIER
  | typeof PERMISSION_ENTITY_FUNDER
  | typeof PERMISSION_ENTITY_ORDER
  | typeof PERMISSION_ENTITY_PURCHASE_ORDER
  | typeof PERMISSION_ENTITY_OPERATIONAL
  | typeof PERMISSION_ENTITY_INVOICE
  | typeof PERMISSION_ENTITY_INVOICE_OFFER
  | typeof PERMISSION_ENTITY_INVOICE_TRANSACTION
  | typeof PERMISSION_ENTITY_PAAS_OFFER
  | typeof PERMISSION_ENTITY_PAYMENT
  | typeof PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE
  | typeof PERMISSION_ENTITY_SOURCE_DOCUMENT
  | typeof PERMISSION_ENTITY_DOCUMENTS
  | typeof PERMISSION_ENTITY_REPORTING
  | typeof PERMISSION_ENTITY_CHANGE_LOG
  | typeof PERMISSION_ENTITY_LEGAL_AGREEMENT_ACCEPTANCE
  | typeof PERMISSION_ENTITY_PLATFORM_FEE
  | typeof PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE
  | typeof PERMISSION_ENTITY_LATE_FEE
  | typeof PERMISSION_ENTITY_SPLIT_ADJUSTMENT

function getCrudPermissions({ add = true, view = true, edit = true, del = true } = {}) {
  const result = [
    add ? PERMISSION_ACTION_ADD : null,
    view ? PERMISSION_ACTION_VIEW : null,
    edit ? PERMISSION_ACTION_EDIT : null,
    del ? PERMISSION_ACTION_DELETE : null
  ].filter(Boolean) as string[]
  return result
}

function getApprovePermissions({ approve = true, deny = true } = {}) {
  const result = [approve ? PERMISSION_ACTION_APPROVE : null, deny ? PERMISSION_ACTION_DENY : null].filter(
    Boolean
  ) as string[]
  return result
}

function getAcceptPermissions({ accept = true, reject = true } = {}) {
  const result = [accept ? PERMISSION_ACTION_ACCEPT : null, reject ? PERMISSION_ACTION_REJECT : null].filter(
    Boolean
  ) as string[]
  return result
}

const readonlyPermissions = {
  [PERMISSION_ENTITY_USER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_ROLE]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_ORGANIZATION]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_TREASURY]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_SETTINGS]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_TRADER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_CUSTOMER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_CREDIT]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_SUPPLIER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_ORDER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_PURCHASE_ORDER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_OPERATIONAL]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_INVOICE]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_INVOICE_OFFER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_PAYMENT]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_REPORTING]: [PERMISSION_ACTION_VIEW]
}

const traderAdminPermissions = {
  [PERMISSION_ENTITY_USER]: getCrudPermissions(),
  [PERMISSION_ENTITY_ROLE]: getCrudPermissions(),
  [PERMISSION_ENTITY_ORGANIZATION]: getCrudPermissions(),
  [PERMISSION_ENTITY_TREASURY]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_TRADER]: getCrudPermissions(),
  [PERMISSION_ENTITY_SETTINGS]: getCrudPermissions(),
  [PERMISSION_ENTITY_CUSTOMER]: getCrudPermissions(),
  [PERMISSION_ENTITY_CREDIT]: getCrudPermissions(),
  [PERMISSION_ENTITY_SUPPLIER]: getCrudPermissions(),
  [PERMISSION_ENTITY_ORDER]: getCrudPermissions(),
  [PERMISSION_ENTITY_PURCHASE_ORDER]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_OPERATIONAL]: getCrudPermissions(),
  [PERMISSION_ENTITY_INVOICE]: getCrudPermissions(),
  [PERMISSION_ENTITY_INVOICE_OFFER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_PAYMENT]: [...getCrudPermissions()],
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_LEGAL_AGREEMENT_ACCEPTANCE]: [...getCrudPermissions()]
}

const traderViewOnlyOrgDirectoryPermissions: Record<string, string[]> = {
  [PERMISSION_ENTITY_USER]: [PERMISSION_ACTION_VIEW],
  [PERMISSION_ENTITY_ROLE]: [PERMISSION_ACTION_VIEW]
}

const traderFinancialAdminEntityOverrides = {
  [PERMISSION_ENTITY_TREASURY]: getCrudPermissions(),
  [PERMISSION_ENTITY_INVOICE_OFFER]: [PERMISSION_ACTION_VIEW, ...getAcceptPermissions()],
  [PERMISSION_ENTITY_PAYMENT]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH, PERMISSION_ACTION_COMPLETE],
  [PERMISSION_ENTITY_PLATFORM_FEE]: [PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: [PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_LATE_FEE]: [PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_SPLIT_ADJUSTMENT]: [PERMISSION_ACTION_PUBLISH],
  [PERMISSION_ENTITY_LEGAL_AGREEMENT_ACCEPTANCE]: [...getCrudPermissions()]
}

export const ROLE_PERMISSIONS: Record<string, Record<string, string[]>> = {
  [ROLE_KLEAR_SUPER_ADMIN]: {},
  [ROLE_KLEAR_CSM]: {
    [PERMISSION_ENTITY_USER]: [
      ...getCrudPermissions(),
      PERMISSION_ACTION_RESET_PASSWORD,
      PERMISSION_ACTION_WELCOME_EMAIL
    ],
    [PERMISSION_ENTITY_ROLE]: getCrudPermissions(),
    [PERMISSION_ENTITY_ORGANIZATION]: getCrudPermissions(),
    [PERMISSION_ENTITY_TREASURY]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SETTINGS]: getCrudPermissions(),
    [PERMISSION_ENTITY_TRADER]: getCrudPermissions(),
    [PERMISSION_ENTITY_CUSTOMER]: getCrudPermissions(),
    [PERMISSION_ENTITY_CREDIT]: getCrudPermissions(),
    [PERMISSION_ENTITY_SUPPLIER]: getCrudPermissions(),
    [PERMISSION_ENTITY_ORDER]: getCrudPermissions(),
    [PERMISSION_ENTITY_PURCHASE_ORDER]: [
      ...getCrudPermissions(),
      ...getApprovePermissions(),
      PERMISSION_ACTION_PUBLISH
    ],
    [PERMISSION_ENTITY_OPERATIONAL]: getCrudPermissions(),
    [PERMISSION_ENTITY_INVOICE]: [...getCrudPermissions(), ...getApprovePermissions(), PERMISSION_ACTION_EXPIRE],
    [PERMISSION_ENTITY_INVOICE_OFFER]: [
      PERMISSION_ACTION_VIEW,
      PERMISSION_ACTION_ADD,
      PERMISSION_ACTION_EDIT,
      PERMISSION_ACTION_VOID
    ],
    [PERMISSION_ENTITY_INVOICE_TRANSACTION]: getCrudPermissions({ edit: false }),
    [PERMISSION_ENTITY_PAAS_OFFER]: [
      PERMISSION_ACTION_VIEW,
      PERMISSION_ACTION_ADD,
      PERMISSION_ACTION_ACCEPT,
      PERMISSION_ACTION_REJECT,
      PERMISSION_ACTION_VOID
    ],
    [PERMISSION_ENTITY_PAYMENT]: getCrudPermissions(),
    [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: [
      ...getCrudPermissions(),
      PERMISSION_ACTION_PUBLISH,
      ...getApprovePermissions()
    ],
    [PERMISSION_ENTITY_SOURCE_DOCUMENT]: [PERMISSION_ACTION_VIEW, PERMISSION_ACTION_ADD, PERMISSION_ACTION_EDIT],
    [PERMISSION_ENTITY_DOCUMENTS]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_REPORTING]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_PLATFORM_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_LATE_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_SPLIT_ADJUSTMENT]: [PERMISSION_ACTION_VIEW]
  },
  [ROLE_KLEAR_READONLY]: {
    ...readonlyPermissions,
    [PERMISSION_ENTITY_INVOICE_TRANSACTION]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_PAAS_OFFER]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SOURCE_DOCUMENT]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_DOCUMENTS]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_PLATFORM_FEE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_LATE_FEE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SPLIT_ADJUSTMENT]: [PERMISSION_ACTION_VIEW]
  },
  [ROLE_KLEAR_FUNDER]: {
    ...readonlyPermissions,
    [PERMISSION_ENTITY_TREASURY]: [PERMISSION_ACTION_VIEW, PERMISSION_ACTION_EDIT],
    [PERMISSION_ENTITY_FUNDER]: [PERMISSION_ACTION_VIEW, PERMISSION_ACTION_ADD, PERMISSION_ACTION_EDIT],
    [PERMISSION_ENTITY_INVOICE_OFFER]: [
      ...getCrudPermissions(),
      PERMISSION_ACTION_VOID,
      PERMISSION_ACTION_FUND,
      PERMISSION_ACTION_COMPLETE
    ],
    [PERMISSION_ENTITY_INVOICE_TRANSACTION]: getCrudPermissions({ edit: false }),
    [PERMISSION_ENTITY_PAAS_OFFER]: [
      PERMISSION_ACTION_VIEW,
      PERMISSION_ACTION_ADD,
      PERMISSION_ACTION_ACCEPT,
      PERMISSION_ACTION_FUND,
      PERMISSION_ACTION_COMPLETE,
      PERMISSION_ACTION_REJECT,
      PERMISSION_ACTION_VOID
    ],
    [PERMISSION_ENTITY_PAYMENT]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH, PERMISSION_ACTION_COMPLETE],
    [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SOURCE_DOCUMENT]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_DOCUMENTS]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_REPORTING]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_CUSTOMER]: getCrudPermissions(),
    [PERMISSION_ENTITY_CREDIT]: getCrudPermissions(),
    [PERMISSION_ENTITY_PLATFORM_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_EXTERNAL_BUFFER_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_LATE_FEE]: getCrudPermissions(),
    [PERMISSION_ENTITY_SPLIT_ADJUSTMENT]: [PERMISSION_ACTION_VIEW]
  },
  [ROLE_TRADER_ADMIN]: {
    ...traderAdminPermissions,
    ...traderViewOnlyOrgDirectoryPermissions
  },
  [ROLE_TRADER_FINANCIAL_ADMIN]: {
    ...traderAdminPermissions,
    ...traderViewOnlyOrgDirectoryPermissions,
    ...traderFinancialAdminEntityOverrides
  },
  [ROLE_TRADER_ORGANIZATION_OWNER]: {
    ...traderAdminPermissions,
    ...traderFinancialAdminEntityOverrides,
    [PERMISSION_ENTITY_USER]: [...traderAdminPermissions[PERMISSION_ENTITY_USER], PERMISSION_ACTION_WELCOME_EMAIL]
  },
  [ROLE_TRADER_USER]: {
    [PERMISSION_ENTITY_USER]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_ROLE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_ORGANIZATION]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_TRADER]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SETTINGS]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_CUSTOMER]: getCrudPermissions(),
    [PERMISSION_ENTITY_CREDIT]: getCrudPermissions(),
    [PERMISSION_ENTITY_SUPPLIER]: getCrudPermissions(),
    [PERMISSION_ENTITY_ORDER]: getCrudPermissions(),
    [PERMISSION_ENTITY_PURCHASE_ORDER]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH],
    [PERMISSION_ENTITY_OPERATIONAL]: getCrudPermissions(),
    [PERMISSION_ENTITY_INVOICE]: getCrudPermissions(),
    [PERMISSION_ENTITY_PAYMENT]: getCrudPermissions(),
    [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH]
  },
  [ROLE_TRADER_READONLY]: {
    ...readonlyPermissions
  },
  [ROLE_TRADER_ACCOUNTS_PAYABLE]: {
    [PERMISSION_ENTITY_USER]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_ROLE]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_ORGANIZATION]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_TRADER]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SETTINGS]: [PERMISSION_ACTION_VIEW],
    [PERMISSION_ENTITY_SUPPLIER]: getCrudPermissions(),
    [PERMISSION_ENTITY_TREASURY]: getCrudPermissions(),
    [PERMISSION_ENTITY_ORDER]: getCrudPermissions(),
    [PERMISSION_ENTITY_OPERATIONAL]: getCrudPermissions(),
    [PERMISSION_ENTITY_PAYMENT]: [...getCrudPermissions(), PERMISSION_ACTION_PUBLISH]
  }
}

export function hasPermission(role: string, entity: string, action: string): boolean {
  if (!role || !entity || !action) {
    throw new Error('Invalid role, entity, or action')
  }
  if (role === ROLE_KLEAR_SUPER_ADMIN) {
    return true
  }
  return ROLE_PERMISSIONS[role]?.[entity]?.includes?.(action) ?? false
}

export const ROUTE_ROOT = '/'
export const ROUTE_TRADER = 'trader'
export const ROUTE_ADMIN = 'admin'

export const ROUTE_CUSTOMERS = 'customers'
export const ROUTE_SUPPLIERS = 'supplier'
export const ROUTE_KLEAR_ACCOUNT = 'klear-account'
export const ROUTE_MAKE_PAYMENT = 'make-payment'
export const ROUTE_TRANSFER_FUNDS = 'transfer-funds'
export const ROUTE_PAYABLES = 'payables'
export const ROUTE_PAYABLE = 'payable'
export const ROUTE_RECEIVABLES = 'receivables'
export const ROUTE_RECEIVABLE = 'receivable'
export const ROUTE_KLEAR_FX = 'klear-fx'
export const ROUTE_ORDERS = 'orders'
export const ROUTE_ORDER_EVENTS = 'order-events'
export const ROUTE_PURCHASE_ORDERS = 'purchase-orders'
export const ROUTE_OPERATIONAL = 'operational'
export const DECIDABLE_DOCUMENT_PROCESSING_STATUSES = ['needs_review', 'failed'] as const
export const UNDERWAY_DOCUMENT_PROCESSING_STATUSES = ['processing'] as const
export const FILING_DOCUMENT_PROCESSING_STATUSES = ['approved', 'executing', 'executed'] as const

export function isUnderwayDocumentProcessingStatus(status: string): boolean {
  return (UNDERWAY_DOCUMENT_PROCESSING_STATUSES as readonly string[]).includes(status)
}

export const ROUTE_OFFERS = 'offers'
export const ROUTE_IDENTITY_VERIFICATION = 'identity-verification'
export const ROUTE_DOCUMENTS = 'documents'
export const ROUTE_DOCUMENT_UPLOAD = 'documents/upload'
export const ROUTE_DOCUMENT_REVIEW = 'documents-review'
export const ROUTE_EXTERNAL_BANK = 'external-bank-account'
export const ROUTE_TEAM = 'team'
export const ROUTE_ORGANIZATION = 'organization'
export const ROUTE_API_DOCUMENTS = 'documents'
export const ROUTE_FILES = 'file'
export const ROUTE_FILE_LEGAL_DOCUMENTS = 'legal-documents'
export const ROUTE_PAYMENT = 'payment'
export const ROUTE_TRANSFERS = 'transfers'
export const ROUTE_TRANSFER = 'transfer'
export const ROUTE_COLLECT = 'collect'
export const ROUTE_COLLECTIONS = 'collections'

export const COLLECTION_QUERY_TYPE_ADVANCE_RECOVERY = 'advance-recovery' as const

export const COLLECTION_WORKFLOW_STEP_SELECT_ITEMS = 'select-items' as const
export const ROUTE_DISBURSE = 'disburse'
export const ROUTE_DISBURSEMENTS = 'disbursements'
export const ROUTE_PROFILE = 'profile'
export const ROUTE_AUTH = 'auth'
export const ROUTE_API = 'api'
export const ROUTE_USER = 'user'
export const ROUTE_SPV_UTILIZATION = 'spv-utilization'

export const ROUTE_API_AGENT = '/api/agent'
export const ROUTE_API_MCP = '/api/mcp'

export const ROUTE_RESET_PASSWORD = 'reset-password'
export const ROUTE_CHANGE_PASSWORD = 'change-password'
export const ROUTE_LOGIN = 'login'
export const ROUTE_SSE_STREAM = 'sse'
export const ROUTE_BILLING = 'billing'
export const ROUTE_REPORTS = 'reports'
export const ROUTE_REPORTS_PAYMENTS = 'payments'
export const ROUTE_REPORTS_FUNDING = 'funding'
export const ROUTE_FUNDER_PRICING = 'funder-pricing'

export const ROUTE_HELP_CENTER = 'https://help.klearbusiness.com/'

export const MCP_SCOPE_READ = 'mcp:read'
export const MCP_SCOPE_WRITE = 'mcp:write'

export const MCP_SUPPORTED_SCOPES: string[] = [MCP_SCOPE_READ, MCP_SCOPE_WRITE]

export function mcpScopeForAction(action: string): string {
  return action === PERMISSION_ACTION_VIEW ? MCP_SCOPE_READ : MCP_SCOPE_WRITE
}

export function mcpScopeGranted(granted: readonly string[], scope: string): boolean {
  if (granted.includes(scope)) return true
  return scope === MCP_SCOPE_READ && granted.includes(MCP_SCOPE_WRITE)
}

export const buildNavPathTrader = (traderId: string, ...urlSegments: string[]) => {
  return ['/' + ROUTE_TRADER, traderId, ...urlSegments].join('/')
}

export function parseOrderIdFromDisplay(displayId: string): number | null {
  if (!displayId.startsWith('TD-')) {
    return null
  }
  const numericPart = parseInt(displayId.substring(3), 10)
  if (isNaN(numericPart)) {
    return null
  }
  return numericPart - ORDER_ID_AUTO_INCREMENT
}

export function formatInvoiceIdDisplay(internalId: number): string {
  return `INV-${internalId + INVOICE_ID_AUTO_INCREMENT}`
}

export type LoggerLevel = 'info' | 'debug' | 'warn' | 'error'

export const CRON_JOB_EVERY_MINUTE = '* * * * *'
export const CRON_JOB_EVERY_5_MINUTES = '*/5 * * * *'
export const CRON_JOB_EVERY_HOUR = '5 * * * *'
export const CRON_JOB_DAILY_9AM = '0 9 * * *'
export const CRON_JOB_WEEKLY_MONDAY_8AM = '0 8 * * 1'
export const CRON_JOB_WEEKLY_WEDNESDAY_2PM = '0 14 * * 3'

export interface ComboboxOption<T = string> {
  value: T
  label: string
  keywords?: string[]
}

export const INCOTERM_EX_WORKS = 'EXW'
export const INCOTERM_FREE_CARRIER = 'FCA'
export const INCOTERM_CARRIAGE_PAID_TO = 'CPT'
export const INCOTERM_CARRIAGE_AND_INSURANCE_PAID_TO = 'CIP'
export const INCOTERM_DELIVERED_AT_TERMINAL = 'DAT'
export const INCOTERM_DELIVERED_AT_PLACE = 'DAP'
export const INCOTERM_DELIVERED_DUTY_PAID = 'DDP'
export const INCOTERM_FREE_ALONGSIDE_SHIP = 'FAS'
export const INCOTERM_FREE_ON_BOARD = 'FOB'
export const INCOTERM_COST_AND_FREIGHT = 'CFR'
export const INCOTERM_COST_INSURANCE_AND_FREIGHT = 'CIF'

export const HEADER_KLEAR_VERSION = 'x-klear-version' as const

export const HEALTH_STATUS_OK = 'ok' as const
export const HEALTH_STATUS_MAINTENANCE = 'maintenance' as const
